import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { PageView } from '@Core/models/proprietary/page/page.state';
import { ObjectRuleSet } from '@Core/services/proprietary/object-rule/object-rule.types';
import { cloneDeep } from 'src/ep-utils';
import { AddObjectRuleSets } from '../page/page.actions';
import { AddWizardPageViews, ResetWizardState, SetCurrentWizardPageView } from './submission-wizard-object.actions';

export interface SubmissionWizardObjectStateModel {
    currentWizardPageView: PageView;
    wizardPageView: any;
}

const SubmissionWizardObjectStateDefaults: SubmissionWizardObjectStateModel = {
    currentWizardPageView: null,
    wizardPageView: null
};

@State<SubmissionWizardObjectStateModel>({
    name: 'submissionWizardObjectState',
    defaults: SubmissionWizardObjectStateDefaults
})
@Injectable()
export class SubmissionWizardObjectState {
    constructor(private store: Store) { }

    @Selector()
    static currentWizardPageView(state: SubmissionWizardObjectStateModel) {
        return state.currentWizardPageView;
    }

    @Selector()
    static wizardPageViews(state: SubmissionWizardObjectStateModel) {
        let result: PageView[] = [];
        for (var i in state.wizardPageView) {
            result.push(state.wizardPageView[i]);
        }
        if (result.length < 2) {
            return [];
        }
        return result;
    }

    @Selector()
    static wizardPageViewAvailableSections(state: SubmissionWizardObjectStateModel) {
        let availableSections: any = [];

        let currentView = state.currentWizardPageView;
        if (currentView.AvailableSections && currentView.AvailableSections.length > 0) {
            availableSections = cloneDeep(currentView.AvailableSections);
        }
        return availableSections;
    }

    @Action(AddWizardPageViews)
    addWizardPageViews(sc: StateContext<SubmissionWizardObjectStateModel>, action: AddWizardPageViews) {
        let state = sc.getState();
        var i: number;
        let changed = {};
        let defaultViewID: number = null;

        for (i = 0; i < action.pageViews.length; i++) {
            let viewID = action.pageViews[i].View_ID;
            changed[viewID] = { ...action.pageViews[i] };
            if (!changed[viewID].DynamicSections) {
                changed[viewID].DynamicSections = []; //initialize to empty array if null
            }
            changed[viewID].RuleSets = this.buildUxConfigRuleSets(changed[viewID]);
            if (
                action.defaultPageLayoutCode &&
                action.pageViews[i].Layout_Object_Code &&
                action.pageViews[i].Layout_Object_Code == action.defaultPageLayoutCode
            ) {
                defaultViewID = viewID;
            }
        }

        for (var idx in changed) {
            changed[idx].AvailableSections = changed[idx].Sections;
            changed[idx].Sections = [];
        }

        sc.patchState({
            wizardPageView: { ...changed },
            currentWizardPageView: null
        });

        if (!defaultViewID) {
            defaultViewID = action.pageViews[0].View_ID;
        }

        sc.dispatch(new SetCurrentWizardPageView(defaultViewID));
    }

    buildUxConfigRuleSets(pageView: PageView): ObjectRuleSet[] {
        let systemRuleSets: ObjectRuleSet[] = [];
        let sectionRuleSets: ObjectRuleSet[] = [];

        //filter out any existing section-based rule sets.   i.e.  keep only system rule sets
        if (pageView && pageView.RuleSets) {
            systemRuleSets = pageView.RuleSets.filter((rs) => rs.source.toLowerCase().startsWith('config') == false);
        }
        //add section-based rule sets
        pageView.Sections.forEach((section) => {
            if (section.Ux_Config) {
                ///iterate through components on section and look for rules nodes
                for (var componentName in section.Ux_Config) {
                    if (section.Ux_Config[componentName].RuleSet) {
                        //TODO:  We should add a section GUID and use the GUID instead of the section title below
                        //       Or perhaps use the GUID from the individual component template
                        sectionRuleSets.push({
                            ...section.Ux_Config[componentName].RuleSet,
                            source: 'Config.' + componentName + '.' + section.DB_Section_ID // section.Title.replace(/\s/g, '')
                        });
                    }
                }
            }
        });

        return [...systemRuleSets, ...sectionRuleSets];
    }

    @Action(SetCurrentWizardPageView)
    SetCurrentWizardPageView(sc: StateContext<SubmissionWizardObjectStateModel>, action: SetCurrentWizardPageView) {
        let state = sc.getState();
        let pageView = state.wizardPageView[action.viewID];
        let ruleSets = pageView.RuleSets;

        if (pageView) {
            sc.patchState({
                currentWizardPageView: pageView
            });
        }

        sc.dispatch(new AddObjectRuleSets(ruleSets, true)); //true indicates reset current rules
    }

    @Action(ResetWizardState)
    ResetWizardState(sc: StateContext<SubmissionWizardObjectStateModel>, action: ResetWizardState) {
        sc.patchState({
            currentWizardPageView: null,
            wizardPageView: null
        });
    }
}
